/**
 * Mappable `VARIATION_NAME_n` / `VARIATION_OPTIONS_n` (n = 1..7) file columns; empty to skip.
 */
export interface BulkProductVariationColumnMapping {
  variationName1: string;
  variationOptions1: string;
  variationName2: string;
  variationOptions2: string;
  variationName3: string;
  variationOptions3: string;
  variationName4: string;
  variationOptions4: string;
  variationName5: string;
  variationOptions5: string;
  variationName6: string;
  variationOptions6: string;
  variationName7: string;
  variationOptions7: string;
}

export function emptyBulkProductVariationColumnMapping(): BulkProductVariationColumnMapping {
  return {
    variationName1: '',
    variationOptions1: '',
    variationName2: '',
    variationOptions2: '',
    variationName3: '',
    variationOptions3: '',
    variationName4: '',
    variationOptions4: '',
    variationName5: '',
    variationOptions5: '',
    variationName6: '',
    variationOptions6: '',
    variationName7: '',
    variationOptions7: '',
  };
}

/**
 * Wire format aligned with `VariationInput`; `id` values are client-generated UUIDs
 * so the API receives stable new ids.
 */
export type BulkProductRowVariation = {
  id: string;
  variation_name: string;
  variation_options: { id: string; variation_value: string }[];
};

function getVariationNameKey(
  m: BulkProductVariationColumnMapping,
  n: number
): string {
  switch (n) {
    case 1:
      return m.variationName1;
    case 2:
      return m.variationName2;
    case 3:
      return m.variationName3;
    case 4:
      return m.variationName4;
    case 5:
      return m.variationName5;
    case 6:
      return m.variationName6;
    case 7:
      return m.variationName7;
    default:
      return '';
  }
}

function getVariationOptionsKey(
  m: BulkProductVariationColumnMapping,
  n: number
): string {
  switch (n) {
    case 1:
      return m.variationOptions1;
    case 2:
      return m.variationOptions2;
    case 3:
      return m.variationOptions3;
    case 4:
      return m.variationOptions4;
    case 5:
      return m.variationOptions5;
    case 6:
      return m.variationOptions6;
    case 7:
      return m.variationOptions7;
    default:
      return '';
  }
}

function cellTrim(rawRow: Record<string, unknown>, key: string | undefined): string {
  if (!key || !String(key).trim()) {
    return '';
  }
  return String(rawRow[key] ?? '').trim();
}

/**
 * If any name/options column (1-7) is mapped, returns true when that cell is non-empty.
 * Used to reject variation data on combination rows.
 */
export function rowHasNonEmptyMappedVariationContent(
  rawRow: Record<string, unknown>,
  m: BulkProductVariationColumnMapping
): boolean {
  for (let n = 1; n <= 7; n++) {
    const nameKey = getVariationNameKey(m, n);
    const optKey = getVariationOptionsKey(m, n);
    if (nameKey.trim() && cellTrim(rawRow, nameKey)) {
      return true;
    }
    if (optKey.trim() && cellTrim(rawRow, optKey)) {
      return true;
    }
  }
  return false;
}

export function allVariationColumnKeys(
  m: BulkProductVariationColumnMapping
): string[] {
  const out: string[] = [];
  for (let n = 1; n <= 7; n++) {
    const nk = getVariationNameKey(m, n);
    const ok = getVariationOptionsKey(m, n);
    if (nk.trim()) {
      out.push(nk);
    }
    if (ok.trim()) {
      out.push(ok);
    }
  }
  return out;
}

function splitOptions(cell: string): string[] {
  if (!cell.trim()) {
    return [];
  }
  return cell
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Loops 1..7, stops at first blank `VARIATION_NAME_n` in the file row (after trim).
 * If the column for `n` is not mapped in the upload mapping, the chain stops.
 * Validates: name without options, options without name.
 */
export function parseBulkVariationNameOptionsForRow(
  rawRow: Record<string, unknown>,
  mapping: BulkProductVariationColumnMapping,
  productSku: string
): { ok: true; variations: BulkProductRowVariation[] } | { ok: false; error: string } {
  const out: BulkProductRowVariation[] = [];
  for (let n = 1; n <= 7; n++) {
    const nameKey = getVariationNameKey(mapping, n);
    if (!nameKey || !nameKey.trim()) {
      if (n === 1) {
        return { ok: true, variations: [] };
      }
      break;
    }
    const optKey = getVariationOptionsKey(mapping, n);
    const nameVal = cellTrim(rawRow, nameKey);
    const optVal = cellTrim(rawRow, optKey);
    if (!nameVal) {
      if (optKey.trim() && optVal) {
        return {
          ok: false,
          error: `Product SKU ${productSku}: VARIATION_OPTIONS_${n} has a value but VARIATION_NAME_${n} is blank. Set VARIATION_NAME_${n} or clear the options cell.`,
        };
      }
      break;
    }
    if (!optKey || !optKey.trim()) {
      return {
        ok: false,
        error: `Product SKU ${productSku}, variation "${nameVal}": map the VARIATION_OPTIONS_${n} file column, or unmap VARIATION_NAME_${n}.`,
      };
    }
    if (!optVal) {
      return {
        ok: false,
        error: `Product SKU ${productSku}, variation "${nameVal}": VARIATION_OPTIONS_${n} is required (comma-separated).`,
      };
    }
    const options = splitOptions(optVal);
    if (options.length === 0) {
      return {
        ok: false,
        error: `Product SKU ${productSku}, variation "${nameVal}": VARIATION_OPTIONS_${n} must have at least one value after comma-splitting and trimming.`,
      };
    }
    out.push({
      id: crypto.randomUUID(),
      variation_name: nameVal,
      variation_options: options.map((variation_value) => ({
        id: crypto.randomUUID(),
        variation_value,
      })),
    });
  }
  return { ok: true, variations: out };
}
